import { useEffect, useState, type FormEvent } from "react";
import { MIN_PASSWORD_LENGTH, TERMS_VERSION, type Me } from "@ai-rpg/protocol";
import { checkInvite, redeemInvite } from "../api.ts";
import { TermsText } from "./Terms.tsx";
import { LOADING } from "../words.ts";

const problemOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

interface Props {
  readonly code: string;
  /** The account is made and signed in. */
  readonly onJoined: (me: Me) => void;
  /** They have an account already: on to signing in. */
  readonly onSignIn: () => void;
}

/** Someone asked here with a link: they choose a name and a password, and have an account. */
export function Invite({ code, onJoined, onSignIn }: Props) {
  const [good, setGood] = useState<boolean | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [adult, setAdult] = useState(false);
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    checkInvite(code)
      .then((g) => live && setGood(g))
      .catch(() => live && setGood(false));
    return () => {
      live = false;
    };
  }, [code]);

  const differ = again.length > 0 && again !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || differ) return;
    setBusy(true);
    setProblem(null);
    try {
      onJoined(await redeemInvite(code, username, password, adult, TERMS_VERSION));
    } catch (err) {
      setProblem(problemOf(err));
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <p className="signin-place">The Table</p>
      <h1 className="lobby-title">You have been asked to the table</h1>
      {good === null ? (
        <p className="quiet" role="status">
          {LOADING}
        </p>
      ) : !good ? (
        <>
          <p className="problem" role="alert">
            That invitation isn't good any more. Ask whoever sent it for a new one.
          </p>
          <button className="link" onClick={onSignIn}>
            I have an account: sign in
          </button>
        </>
      ) : (
        <>
          <p className="lobby-lede">
            Choose the name you will sign in with and a password. Your characters and your games are kept under that
            name.
          </p>
          <form className="form" onSubmit={submit}>
            <label className="field">
              <span className="field-label">Username</span>
              <input
                className="input"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                required
                maxLength={64}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Password</span>
              <input
                className="input"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="field-note">At least {MIN_PASSWORD_LENGTH} characters.</span>
            </label>
            <label className="field">
              <span className="field-label">The password again</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={again}
                onChange={(e) => setAgain(e.target.value)}
              />
              {differ && <span className="field-note">The two don't match yet.</span>}
            </label>
            {TERMS_VERSION === null ? (
              <label className="agree-tick">
                <input type="checkbox" required checked={adult} onChange={(e) => setAdult(e.target.checked)} />
                <span>I am 18 or older.</span>
              </label>
            ) : (
              <>
                <div className="terms-frame" tabIndex={0}>
                  <TermsText />
                </div>
                <label className="agree-tick">
                  <input type="checkbox" required checked={adult} onChange={(e) => setAdult(e.target.checked)} />
                  <span>I agree to these terms, and I am 18 or older.</span>
                </label>
              </>
            )}
            <button
              className="button button-primary"
              type="submit"
              disabled={busy || !adult || !username || password.length < MIN_PASSWORD_LENGTH || again !== password}
            >
              {busy ? "Making your account…" : "Make my account"}
            </button>
            {problem && (
              <p className="problem" role="alert">
                {problem}
              </p>
            )}
          </form>
          <button className="link" onClick={onSignIn}>
            I have an account already: sign in
          </button>
        </>
      )}
    </main>
  );
}
